// src/components/Console/Staff/roles/ReceptionTasks/pages/ReceptionBookings.jsx
import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import moment from 'moment-timezone';
import { StateContext } from '../../../../../../StateProvider/index.js';
import { fetchBookings } from '../../../../../../Utilities/api/index.js';
import ReceptionBookingSummary from './ReceptionBookingSummary.js';
import { BackButton } from '../../../../../../Utilities/components/Buttons/index.js';
import './ReceptionBookings.css';

const ReceptionBookings = function () {
  var navigate = useNavigate();
  var ctx = useContext(StateContext) || {};
  var setGlobalLoading = ctx.setLoading || function () {};
  var [date, setDate] = useState(new Date());
  var [activeDate, setActiveDate] = useState(null);
  var [selectedDateBookings, setSelectedDateBookings] = useState([]);
  var [bookings, setBookings] = useState([]);
  var [loadingLocal, setLoadingLocal] = useState(true);
  var [error, setError] = useState(null);

  // filter state: { kind: 'all' | 'type' | 'status', value: string|null }
  var [filter, setFilter] = useState({ kind: 'all', value: null });

  // unfiltered summary for the active day
  var [daySummary, setDaySummary] = useState({
    total: 0,
    byType: {},
    byStatus: {},
    uniqueClients: 0,
    revenueOriginal: 0,
    revenueDiscounted: 0
  });

  useEffect(function () {
    var mounted = true;

    async function load() {
      setGlobalLoading(true);
      setLoadingLocal(true);
      setError(null);
      try {
        var data = await fetchBookings();
        if (!mounted) return;

        var normalized = [];
        if (Array.isArray(data)) {
          for (var i = 0; i < data.length; i++) {
            var b = data[i];
            normalized.push({
              id: b.id,
              userId: b.userid ? b.userid : b.user_id,
              startTime: b.startTime ? b.startTime : b.start_time,
              endTime: b.endTime ? b.endTime : b.end_time,
              bookingType: b.bookingType ? b.bookingType : b.booking_type,
              selectedPets: normalizeSelectedPets(
                b.selectedPets ? b.selectedPets : b.selected_pets
              ),
              status: b.status || 'Pending',

              transactionStatus:
                b.transactionStatus ||
                b.transaction_status ||
                'Pending',

              originalPrice: Number(
                b.originalPrice || b.original_price || 0
              ),
              discountedPrice: Number(
                b.discountedPrice ||
                b.discounted_price ||
                b.originalPrice ||
                b.original_price ||
                0
              )
            });
          }
        }

        setBookings(normalized);

        var todayIso = moment().tz(moment.tz.guess()).format('YYYY-MM-DD');
        setActiveDate(todayIso);

        var todaysBase = filterByDate(normalized, todayIso);
        setDaySummary(buildSummary(todaysBase));
        setSelectedDateBookings(applyFilter(todaysBase, filter));
      } catch (err) {
        setError(err && err.message ? err.message : 'Failed to load bookings.');
      } finally {
        setLoadingLocal(false);
        setGlobalLoading(false);
      }
    }

    load();
    return function () { mounted = false; };
  }, [setGlobalLoading]);

  function normalizeSelectedPets(petsRaw) {
    if (!petsRaw) return [];
    if (Array.isArray(petsRaw)) return petsRaw;
    try {
      var parsed = JSON.parse(petsRaw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function petLabel(pet) {
    if (!pet) return '';
    if (typeof pet === 'string') return pet;
    // support { petName } or { name }
    return pet.petName ? pet.petName : (pet.name ? pet.name : '');
  }

  function formatTime(inputDate) {
    try {
      return moment(inputDate).format('h:mm A');
    } catch (e) {
      return '';
    }
  }

  function filterByDate(list, isoDate) {
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var bk = list[i];
      var d = moment(bk.startTime).tz(moment.tz.guess()).format('YYYY-MM-DD');
      if (bk.status !== 'Canceled' && d === isoDate) out.push(bk);
    }
    return out;
  }

  function pendingFirst(list) {
    return list.slice().sort(function (a, b) {
      var aPending = a.transactionStatus === 'Completed' ? 1 : 0;
      var bPending = b.transactionStatus === 'Completed' ? 1 : 0;
      if (aPending !== bPending) return aPending - bPending;
      return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
    });
  }

  function applyFilter(list, filterObj) {
    if (!filterObj || filterObj.kind === 'all') return pendingFirst(list);

    var out = [];
    for (var i = 0; i < list.length; i++) {
      var b = list[i];
      if (filterObj.kind === 'type') {
        if ((b.bookingType || '') === filterObj.value) out.push(b);
      } else if (filterObj.kind === 'status') {
        if ((b.status || '') === filterObj.value) out.push(b);
      }
    }
    return pendingFirst(out);
  }

  function buildSummary(list) {
    var byType = {};
    var byStatus = {};
    var users = {};
    var revO = 0;
    var revD = 0;

    for (var i = 0; i < list.length; i++) {
      var b = list[i];
      var t = b.bookingType || '';
      var s = b.status || '';
      byType[t] = (byType[t] || 0) + 1;
      byStatus[s] = (byStatus[s] || 0) + 1;
      if (b.userId) users[b.userId] = true;
      revO += Number(b.originalPrice || 0);
      revD += Number(b.discountedPrice || 0);
    }

    return {
      total: list.length,
      byType: byType,
      byStatus: byStatus,
      uniqueClients: Object.keys(users).length,
      revenueOriginal: revO,
      revenueDiscounted: revD
    };
  }

  function goToCreateBooking() {
    navigate('/console/tasks/reception/reception-bookings/create');
  }

  function handleDateChange(selectedDate) {
    var localDate = moment(selectedDate).tz(moment.tz.guess()).startOf('day').toDate();
    setDate(localDate);
    var iso = localDate.toISOString().split('T')[0];
    setActiveDate(iso);

    var base = filterByDate(bookings, iso);
    setDaySummary(buildSummary(base));
    setSelectedDateBookings(applyFilter(base, filter));
  }

  function handleFilterChange(nextFilter) {
    setFilter(nextFilter);

    // re-evaluate for the active day
    if (!activeDate) return;
    var base = filterByDate(bookings, activeDate);
    // summary stays unfiltered
    setSelectedDateBookings(applyFilter(base, nextFilter));
  }

  function tileContent(tileInfo) {
    var dateObj = tileInfo.date;
    var view = tileInfo.view;
    if (view !== 'month') return null;

    var localDate = moment(dateObj).tz(moment.tz.guess()).startOf('day').toDate();
    var formattedDate = localDate.toISOString().split('T')[0];
    var today = moment().tz(moment.tz.guess()).startOf('day').toISOString().split('T')[0];
    var isActive = formattedDate === activeDate;

    var dayBookings = filterByDate(bookings, formattedDate);

    var tileClassName = '';
    if (formattedDate < today && dayBookings.length > 0) {
      tileClassName = isActive ? 'history-tile-active' : 'history-tile';
    } else if (formattedDate === today) {
      tileClassName = isActive ? 'today-tile-active' : 'today-tile';
    } else if (dayBookings.length > 0) {
      tileClassName = isActive ? 'booked-tile-active' : 'booked-tile';
    }

    if (tileClassName && !isActive) {
      return (
        <div className={tileClassName}>
          <abbr className="reserved-abbr" aria-label={formattedDate}>
            {localDate.getDate()}
          </abbr>
        </div>
      );
    }
    return <div className={tileClassName}></div>;
  }

  function goToBookingDetails(bk) {
    if (bk && bk.userId) {
      navigate('/console/userPaymentDetails/' + bk.userId + '/' + bk.id, { state: { transaction: bk } });
    } else {
      navigate('/console/booking/' + bk.id, { state: { booking: bk } });
    }
  }

  return (
    <div className="events-container">
      <div className="events-header">
        <BackButton />

        <button
          className="booking-create-button"
          onClick={goToCreateBooking}
        >
          + New Booking
        </button>
      </div>
      <div className="legend">
        <div className="legend-item"><span className="history-dot" /> History</div>
        <div className="legend-item"><span className="today-dot" /> Today</div>
        <div className="legend-item"><span className="booked-dot" /> Booked</div>
        <div className="legend-item"><span className="events-dot" /> Events</div>
      </div>

      <div className="calendar-container">
        <Calendar
          calendarType="gregory"
          onChange={handleDateChange}
          value={date}
          tileContent={tileContent}
        />
      </div>

      <div className="event-details">
        <h3 className="event-details-header">
          {activeDate ? moment(activeDate).format('MMMM D, YYYY') + ' Overview' : 'Day Overview'}
        </h3>

        {/* Summary with filter chips */}
        <ReceptionBookingSummary
          dateISO={activeDate}
          bookings={selectedDateBookings /* optional if your summary wants to derive anything */}
          activeFilter={filter}          /* { kind, value } */
          onFilter={handleFilterChange}  /* callback from chips */
        />

        <hr className="booking-hr" />

        {loadingLocal && <p className="event-details-message">Loading bookings…</p>}
        {error && <p className="event-details-message" style={{ color: 'red' }}>{error}</p>}

        {!loadingLocal && !error && selectedDateBookings.length > 0 ? (
          <ul>
            {selectedDateBookings.map(function (bk) {
              return (
                <li
                  key={bk.id}
                  className={
                    'event-details-item ' +
                    (
                      bk.transactionStatus === 'Completed'
                        ? 'booking-paid'
                        : 'booking-pending'
                    )
                  }
                >
                  <div className="date">{formatTime(bk.startTime)}</div>
                  <div className="payment-title-row">
                    <div className="title">{bk.bookingType}</div>

                    <span
                      className={
                        bk.transactionStatus === 'Completed'
                          ? 'payment-badge payment-paid'
                          : 'payment-badge payment-pending'
                      }
                    >
                      {bk.transactionStatus === 'Completed' ? 'Paid' : 'Pending'}
                    </span>
                  </div>
                  <div className="description">
                    {(bk.selectedPets || []).map(function (pet, idx) {
                      return <p key={idx} className="event-details-pet-name">({petLabel(pet)})</p>;
                    })}
                  </div>
                  <button onClick={function () { goToBookingDetails(bk); }}>View</button>
                </li>
              );
            })}
          </ul>
        ) : (
          !loadingLocal && !error && <p className="event-details-message">No visits or events for the selected date</p>
        )}
      </div>
    </div>
  );
};

export default ReceptionBookings;
